import { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import secret from "../../../secret.json";

import { retrieveCommentaryPDFAPI } from "../../worksheets/worksheetAPI";
import {
  fetchLevels,
  fetchStudents,
  fetchAllUsers,
  setSelectedUser,
  fetchAssignments,
} from "../adminSlice";

import { refinePages } from "../../../utils/paginationUtils";
import {
  formatFullDateString,
  formatDateString,
  showStudentURL,
} from "../../../utils/textUtils";

import FilteringTab from "../../worksheets/components/common/FilteringTab/FilteringTab";
import WorksheetTypeBox from "../../worksheets/components/common/WorksheetTypeBox";
import WorksheetSearchBar from "../../worksheets/components/common/WorksheetSearchBar";
import LinkCopyButton from "../../../components/complex/LinkCopyButton";
import ReviewPageButton from "../../../components/complex/ReviewPageButton";
import CommentaryPDFButton from "../../../components/complex/CommentaryPDFButton";
import Pagination from "../../../components/Pagination";
import UserSelectBar from "./UserSelectBar";

const AdminPage = () => {
  const dispatch = useDispatch();

  const {
    results,
    current_page,
    total_pages,
    levels,
    allStudents,
    users,
    selectedUser,
  } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  const [keyword, setKeyword] = useState("");
  const [selectedParams, setSelectedParams] = useState({
    levelId: null,
    studentId: null,
    studentGrade: null,
  });

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handleUserSelect = (userId) => {
    const selectedUser = users.filter(
      (u) => u.id === parseInt(userId, 10)
    )[0] ?? {
      id: null,
      nickname: "",
    };
    dispatch(setSelectedUser(selectedUser));
    setSelectedParams({ levelId: null, studentId: null, studentGrade: null });
    setKeyword("");
  };

  const showCommentaryPDF = async (assignment) => {
    const pdfWindow = window.open();
    const response = await retrieveCommentaryPDFAPI(
      assignment.id,
      assignment.student.id
    );
    if (response.status !== 200) alert("해설지를 불러오는데 실패했습니다.");

    const file = new Blob([response.data], {
      type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(file);
    pdfWindow.location.href = fileURL;
  };

  const showReviewPage = (assignment) => {
    if (!assignment.finished_at) return;
    const reviewURL = `${secret.STUDENT_BASE_URL}/review?id=${assignment.id}&session=${user.session}`;
    window.open(reviewURL, "_blank");
  };

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignments({
        page,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
  };

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser.id) {
      dispatch(
        fetchAssignments({
          page: 1,
          keyword: keyword,
          levelId: selectedParams.levelId,
          studentId: selectedParams.studentId,
          studentGrade: selectedParams.studentGrade,
        })
      );
      dispatch(fetchLevels());
      dispatch(fetchStudents());
    }
  }, [dispatch, selectedUser, keyword, selectedParams]);

  return (
    <div
      style={{
        padding: "10px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <h3 style={{ marginRight: "auto" }}>학원별 출제 내역</h3>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <div style={{ width: "50%" }}>
            <WorksheetSearchBar handleFilter={setKeyword} />
          </div>
          <UserSelectBar
            users={users}
            selectedUser={selectedUser}
            handleUserSelect={handleUserSelect}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          marginTop: "10px",
        }}
      >
        <FilteringTab
          levels={levels}
          allStudentList={allStudents}
          selectedParams={selectedParams}
          setSelectedParams={setSelectedParams}
        />
        <div
          style={{
            width: "85%",
            backgroundColor: "#FFF",
            paddingBottom: "5px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <colgroup>
              <col style={{ width: "12%" }} />
              <col />
              <col style={{ width: "8%" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "66px" }} />
              <col style={{ width: "180px" }} />
            </colgroup>
            <tbody>
              <tr
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #EBEBEB",
                  fontSize: "16px",
                  color: "#737373",
                }}
              >
                <td style={{ padding: "10px" }}>이름</td>
                <td style={{ padding: "10px", paddingLeft: "15px" }}>
                  학습지명
                </td>
                <td style={{ padding: "10px", textAlign: "center" }}>마감일</td>
                <td style={{ padding: "10px", textAlign: "center" }}>채점</td>
                <td style={{ padding: "10px", textAlign: "center" }}>해설지</td>
                <td style={{ padding: "10px", textAlign: "center" }}>
                  제출 시간
                </td>
              </tr>
              {results &&
                results.map((item, index) => {
                  const student = allStudents.find(
                    (student) => student.id === item.student.id
                  );
                  return (
                    <tr
                      key={item.id}
                      style={{
                        fontSize: "18px",
                      }}
                    >
                      <td style={{ padding: "10px" }}>{student?.name || ""}</td>
                      <td
                        style={{
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <LinkCopyButton
                          URL={showStudentURL(item.student.session)}
                        />
                        {item.title}
                        <div style={{ marginLeft: "10px" }}>
                          <WorksheetTypeBox type={item.type} />
                        </div>
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {formatDateString(item.closed_at)}
                      </td>
                      <td style={{ padding: "10px" }}>
                        <ReviewPageButton
                          assignment={item}
                          showReviewPage={showReviewPage}
                        />
                      </td>
                      <td style={{ textAlign: "center", position: "relative" }}>
                        {item.commentary_pdf_path !== "" && (
                          <CommentaryPDFButton
                            assignment={item}
                            showCommentaryPDF={showCommentaryPDF}
                          />
                        )}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          fontSize: "16px",
                          textAlign: "center",
                        }}
                      >
                        {formatFullDateString({
                          dateString: item.finished_at,
                          defaultString: "미제출",
                        })}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          <Pagination
            visiblePages={visiblePages}
            current_page={current_page}
            handlePageClick={handlePageClick}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
